import {
  BehaviorSubject,
  Observable,
  Subscription,
  catchError,
  finalize,
  map,
  of,
  timer
} from 'rxjs';
import { EmbedMessageToParentStatus, EmbedSessionStatus } from '@app/core/models/embed.model';
import {
  BenefitChecksPageAccess,
  BenefitChecksPagesEnum,
  GroupEmbedPageAccess,
  GroupEmbedPagesEnum,
  PatientDetailsPageAccess,
  PatientDetailsPagesEnum,
  PayerCredentialPageAccess,
  PayerCredentialPagesEnum,
  ProviderManagementPageAccess,
  ProviderManagementPagesEnum
} from '@app/core/models/route.model';
import { HttpMethod, HttpService } from '../http/http.service';
import {
  defaultAppFeaturesAccess,
  defaultGroupEmbedAccessAllowed,
  defaultGroupEmbedAccessNotAllowed,
  defaultPayerCredentialPageAccessAllowed,
  defaultPayerCredentialPageAccessNotAllowed,
  defaultProviderManagementPageAccessNotAllowed
} from '@app/core/constants/route.constants';

import { APIConstants } from '@app/core/constants/api.constants';
import { AppConstants } from '@app/core/constants/app.constants';
import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { JwtPayload } from 'jwt-decode';
import { JwtUtils } from '@app/shared/utils/jwt.utils';
import { LocalStorageService } from '../storage/storage.service';
import { SharedMethods } from '@app/shared/utils/shared-methods';

enum AppointmentDetailsFeature {
  DailyHuddle = 'daily-huddle',
  Appointment = 'appointment',
  BasicEligibility = 'basic-eligibility',
  PatientPlan = 'patient-plan',
  Coverages = 'coverages',
  EligibilityLog = 'eligibility-log',
  Resync = 'resync',
  NewEligibilityCheck = 'new-eligibility-check',
  BenefitEdit = 'benefit-edit',
  AppointmentNote = 'appointment-note',
  StatusChange = 'status-change',
  DownloadAppointment = 'download-appointment',
  Patients = 'patients',
  InformationIcon = 'information-icon'
}

enum EmbedPayerRequestFeature {
  ListPayerCredential = 'list-payer-credential',
  UpdatePayerCredential = 'update-payer-credential',
  AddPayerCredential = 'add-payer-credential',
  DeletePayerCredential = 'delete-payer-credential',
  RequestEmail = 'mfa-management'
}

enum EmbedBenefitChecksFeature {
  YearlyBenefitCheck = 'yearly-benefit-check'
}

enum EmbedProviderRequestFeature {
  ProviderManagement = 'provider-management',
  AddUpdateProvider = 'add-update-provider'
}

export type AppEmbedFeature =
  | AppointmentDetailsFeature
  | EmbedPayerRequestFeature
  | EmbedBenefitChecksFeature
  | EmbedProviderRequestFeature;

const appointmentFeaturePageMap: Record<
  AppointmentDetailsFeature,
  PatientDetailsPagesEnum | GroupEmbedPagesEnum
> = {
  [AppointmentDetailsFeature.Appointment]: PatientDetailsPagesEnum.Appointment,
  [AppointmentDetailsFeature.BasicEligibility]: PatientDetailsPagesEnum.BasicEligibility,
  [AppointmentDetailsFeature.PatientPlan]: PatientDetailsPagesEnum.PatientPlan,
  [AppointmentDetailsFeature.Coverages]: PatientDetailsPagesEnum.Coverages,
  [AppointmentDetailsFeature.EligibilityLog]: PatientDetailsPagesEnum.EligibilityLogs,
  [AppointmentDetailsFeature.AppointmentNote]: GroupEmbedPagesEnum.AppointmentNote,
  [AppointmentDetailsFeature.BenefitEdit]: GroupEmbedPagesEnum.BenefitEdit,
  [AppointmentDetailsFeature.DailyHuddle]: GroupEmbedPagesEnum.DailyHuddle,
  [AppointmentDetailsFeature.NewEligibilityCheck]: GroupEmbedPagesEnum.NewEligibilityCheck,
  [AppointmentDetailsFeature.Resync]: GroupEmbedPagesEnum.Resync,
  [AppointmentDetailsFeature.StatusChange]: GroupEmbedPagesEnum.StatusChange,
  [AppointmentDetailsFeature.DownloadAppointment]: GroupEmbedPagesEnum.DownloadAppointment,
  [AppointmentDetailsFeature.Patients]: GroupEmbedPagesEnum.Patients,
  [AppointmentDetailsFeature.InformationIcon]: GroupEmbedPagesEnum.InformationIcon
};

const payerCredentialFeaturePageMap: Record<EmbedPayerRequestFeature, PayerCredentialPagesEnum> = {
  [EmbedPayerRequestFeature.AddPayerCredential]: PayerCredentialPagesEnum.AddPayerCredential,
  [EmbedPayerRequestFeature.DeletePayerCredential]: PayerCredentialPagesEnum.DeletePayerCredential,
  [EmbedPayerRequestFeature.ListPayerCredential]: PayerCredentialPagesEnum.ListPayerCredential,
  [EmbedPayerRequestFeature.UpdatePayerCredential]: PayerCredentialPagesEnum.UpdatePayerCredential,
  [EmbedPayerRequestFeature.RequestEmail]: PayerCredentialPagesEnum.RequestEmail
};

const benefitChecksFeaturePageMap: Record<EmbedBenefitChecksFeature, BenefitChecksPagesEnum> = {
  [EmbedBenefitChecksFeature.YearlyBenefitCheck]: BenefitChecksPagesEnum.YearlyBenefitCheck
};

const providerManagementFeaturePageMap: Record<
  EmbedProviderRequestFeature,
  ProviderManagementPagesEnum
> = {
  [EmbedProviderRequestFeature.ProviderManagement]: ProviderManagementPagesEnum.ProviderManagement,
  [EmbedProviderRequestFeature.AddUpdateProvider]: ProviderManagementPagesEnum.AddUpdateProvider
};

const appFeaturePageMap: Record<
  AppEmbedFeature,
  | PatientDetailsPagesEnum
  | GroupEmbedPagesEnum
  | PayerCredentialPagesEnum
  | BenefitChecksPagesEnum
  | ProviderManagementPagesEnum
> = {
  ...appointmentFeaturePageMap,
  ...payerCredentialFeaturePageMap,
  ...benefitChecksFeaturePageMap,
  ...providerManagementFeaturePageMap
};

type EmbedAuthJwtPayload = JwtPayload & {
  data: string;
  features:
    | AppointmentDetailsFeature[]
    | EmbedPayerRequestFeature[]
    | EmbedBenefitChecksFeature[]
    | EmbedProviderRequestFeature[];
};

@Injectable({
  providedIn: 'root'
})
export class EmbedAuthService {
  private _isEmbedded = true;
  private _isTokenVerified = false;
  private _isAuthenticated = false;
  private _authToken: string | undefined;
  private _allowedPages: PatientDetailsPageAccess &
    GroupEmbedPageAccess &
    PayerCredentialPageAccess &
    BenefitChecksPageAccess &
    ProviderManagementPageAccess = defaultAppFeaturesAccess;
  private _allowedPayerCredentialPages: PayerCredentialPageAccess =
    defaultPayerCredentialPageAccessAllowed;
  private _tokenExpirationSubscription: Subscription | null = null;
  private _embedSessionStatusSubject: BehaviorSubject<EmbedSessionStatus> =
    new BehaviorSubject<EmbedSessionStatus>(EmbedSessionStatus.Inactive); // Stores if the embed session is valid.
  private _refreshTokenTimerSubscription: Subscription | null = null;
  private _apptEmbedUrl: string | null = null;

  constructor(private _http: HttpService, private storageService: LocalStorageService) {
    this._isEmbedded = window.self !== window.top;
    if (this._isEmbedded) {
      this._embedSessionStatusSubject.next(EmbedSessionStatus.Active);
    }
  }

  isAuthenticated(
    jwtString?: string | null,
    isPayerCredential?: boolean | null,
    apptUrl?: string | null
  ): Observable<boolean> {
    if (!this._isEmbedded) {
      return of(false);
    }
    if (this._isTokenVerified) {
      return of(this._isAuthenticated);
    }
    if (!jwtString) return of(false);
    this._authToken = jwtString;

    if (apptUrl) {
      this._apptEmbedUrl = apptUrl;
    }
    console.log('Appt Embed URL:', this._apptEmbedUrl);

    return this._http
      .sendSecureRequest<{ jwtToken: string; jwtExpiration: string }>({
        method: HttpMethod.POST,
        headers: new HttpHeaders(),
        url: APIConstants.embedEligibilityValidateToken
      })
      .pipe(
        map((response) => {
          jwtString = response.body?.data.jwtToken;
          if (!jwtString) return false;
          this._isAuthenticated = true;
          const jwtPayload = JwtUtils.decodeToken(jwtString) as EmbedAuthJwtPayload;
          this._authToken = jwtString;

          this.setTokenExpirationAlert(jwtPayload.exp);
          this.clearStorage();
          // this.startRefreshTokenTimer();

          const allowedPages: PatientDetailsPageAccess &
            GroupEmbedPageAccess &
            PayerCredentialPageAccess &
            BenefitChecksPageAccess &
            ProviderManagementPageAccess = {
            ...defaultAppFeaturesAccess
          };

          jwtPayload.features.forEach((feature) => {
            if (this.isAppointmentDetailsFeature(feature)) {
              const pageKey = appointmentFeaturePageMap[feature];
              allowedPages[pageKey] = true;
            } else if (this.isEmbedPayerRequestFeature(feature)) {
              const pageKey = payerCredentialFeaturePageMap[feature];
              allowedPages[pageKey] = true;
              isPayerCredential = true;
            } else if (this.isBenefitChecksFeature(feature)) {
              const pageKey = benefitChecksFeaturePageMap[feature];
              allowedPages[pageKey] = true;
            } else if (this.isProviderManagementFeature(feature)) {
              const pageKey = providerManagementFeaturePageMap[feature];
              allowedPages[pageKey] = true;
            }
          });

          if (isPayerCredential) {
            const allowedPages = defaultPayerCredentialPageAccessNotAllowed;
            jwtPayload.features.forEach((feature) => {
              allowedPages[payerCredentialFeaturePageMap[feature as EmbedPayerRequestFeature]] =
                true;
            });
            this._allowedPayerCredentialPages = allowedPages;
          }
          this._allowedPages = allowedPages;
          return true;
        }),
        catchError(() => {
          this._isAuthenticated = false;
          this._embedSessionStatusSubject.next(EmbedSessionStatus.Invalid);
          SharedMethods.checkAndSendMessageToParent(EmbedMessageToParentStatus.SessionExpired);
          return of(false);
        }),
        finalize(() => {
          this._isTokenVerified = true;
        })
      );
  }

  refreshToken(): Observable<boolean> {
    if (!this._isEmbedded || !this._authToken) {
      return of(false);
    }
    return this._http
      .sendSecureRequest<{ jwtToken: string; jwtExpiration: string }>({
        method: HttpMethod.POST,
        headers: new HttpHeaders(),
        url: APIConstants.embedEligibilityRefreshToken
      })
      .pipe(
        map((response) => {
          const jwtString = response.body?.data.jwtToken;
          if (!jwtString) return false;
          this._isAuthenticated = true;
          const jwtPayload = JwtUtils.decodeToken(jwtString) as EmbedAuthJwtPayload;
          this._authToken = jwtString;

          this.setTokenExpirationAlert(jwtPayload.exp);
          this.startRefreshTokenTimer();

          const allowedPages: PatientDetailsPageAccess &
            GroupEmbedPageAccess &
            PayerCredentialPageAccess &
            BenefitChecksPageAccess &
            ProviderManagementPageAccess = {
            ...defaultAppFeaturesAccess
          };
          jwtPayload.features.forEach((feature) => {
            if (appFeaturePageMap[feature as AppEmbedFeature])
              allowedPages[appFeaturePageMap[feature]] = true;
          });
          this._allowedPages = allowedPages;
          return true;
        }),
        catchError(() => {
          this._isAuthenticated = false;
          this._embedSessionStatusSubject.next(EmbedSessionStatus.Invalid);
          SharedMethods.checkAndSendMessageToParent(EmbedMessageToParentStatus.SessionExpired);
          return of(false);
        }),
        finalize(() => {
          this._isTokenVerified = true;
        })
      );
  }

  isAppointmentDetailsFeature(feature: string): feature is AppointmentDetailsFeature {
    return Object.values(AppointmentDetailsFeature).includes(feature as AppointmentDetailsFeature);
  }

  isEmbedPayerRequestFeature(feature: string): feature is EmbedPayerRequestFeature {
    return Object.values(EmbedPayerRequestFeature).includes(feature as EmbedPayerRequestFeature);
  }

  isBenefitChecksFeature(feature: string): feature is EmbedBenefitChecksFeature {
    return Object.values(EmbedBenefitChecksFeature).includes(feature as EmbedBenefitChecksFeature);
  }

  isProviderManagementFeature(feature: string): feature is EmbedProviderRequestFeature {
    return Object.values(EmbedProviderRequestFeature).includes(
      feature as EmbedProviderRequestFeature
    );
  }

  private startRefreshTokenTimer(): void {
    this.cancelRefreshTokenTimer();

    // Start a new timer
    const refreshTimeInSeconds = AppConstants.refreshTimeInSeconds;

    this._refreshTokenTimerSubscription = timer(refreshTimeInSeconds * 1000).subscribe(() => {
      this.refreshToken().subscribe();
    });
  }

  cancelRefreshTokenTimer(): void {
    if (this._refreshTokenTimerSubscription) {
      this._refreshTokenTimerSubscription.unsubscribe();
      this._refreshTokenTimerSubscription = null;
      SharedMethods.checkAndSendMessageToParent(EmbedMessageToParentStatus.SessionExpired);
    }
  }

  setSessionInvalid(): void {
    this._embedSessionStatusSubject.next(EmbedSessionStatus.Expired);
    this.cancelRefreshTokenTimer();
    this.clearStorage();
  }
  isEmbedded(): boolean {
    return this._isEmbedded;
  }

  isAuthTokenValid(): boolean {
    return !this._authToken ? false : !JwtUtils.isTokenExpired(this._authToken);
  }

  getEmbedSessionValidity() {
    return this._embedSessionStatusSubject.asObservable();
  }

  getAuthToken(): string | undefined {
    return this._authToken;
  }
  getAllowedPages(): PatientDetailsPageAccess &
    GroupEmbedPageAccess &
    PayerCredentialPageAccess &
    BenefitChecksPageAccess &
    ProviderManagementPageAccess {
    return { ...this._allowedPages };
  }

  getPayerCredentialAllowedPages(): PayerCredentialPageAccess {
    return this._allowedPayerCredentialPages;
  }

  getApptEmbedUrl(): string | null {
    return this._apptEmbedUrl;
  }

  clearStorage() {
    [
      AppConstants.localStorageUserEulaState,
      AppConstants.localStorageIsFeatureRealtime,
      AppConstants.localStorageLocationId,
      AppConstants.localStorageSelectedHuddleFilters,
      AppConstants.localStoragePayerCredentialPageFeature,
      AppConstants.localStorageUserPilotSessionGroup
    ].map((e) => this.storageService.remove(e));
  }

  /**
   * Sets a timer which handles JWT token expiration based on given expiration time.
   * @param exp expiration time in seconds.
   */
  private setTokenExpirationAlert(exp?: number): void {
    if (!exp) return;

    const expirationTime = exp * 1000; // Convert seconds to milliseconds
    const currentTime = Date.now();
    const timeRemaining = expirationTime - currentTime;

    // Clear any previous timeout
    this._tokenExpirationSubscription?.unsubscribe();

    this._tokenExpirationSubscription = timer(timeRemaining).subscribe(() => {
      this._embedSessionStatusSubject.next(EmbedSessionStatus.Expired);
      SharedMethods.checkAndSendMessageToParent(EmbedMessageToParentStatus.SessionExpired);
    });
  }
}
